// pages/ResetPassword.tsx
import { useEffect, useRef, useState, useMemo } from "react";
import { Link, useLocation, useRoute } from "wouter";
import Layout from "@/components/Layout";
import SEO from '@/components/SEO'; // SEO: reset password page -> noindex,nofollow
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

function useQueryParam(name: string) {
  const [value, setValue] = useState<string | null>(null);
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      setValue(params.get(name));
    } catch {
      setValue(null);
    }
  }, [name]);
  return value;
}

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const tokenFromQuery = useQueryParam("token");
  const [, params] = useRoute("/reset-password/:token");
  const token = tokenFromQuery || params?.token || null;
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  const firstInvalidRef = useRef<HTMLInputElement | null>(null);

  // Simple strength evaluator (visual only, not gating). Mirrors Register minimal policy.
  function getPasswordStrength(pwd: string): '' | 'weak' | 'medium' | 'strong' {
    if (!pwd) return '';
    const lengthScore = pwd.length >= 12 ? 2 : pwd.length >= 8 ? 1 : 0;
    const variety = [/[_a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].reduce((acc, r) => acc + (r.test(pwd) ? 1 : 0), 0);
    const score = lengthScore + variety; // 0-6 approx
    if (pwd.length >= 6 && score >= 5) return 'strong';
    if (pwd.length >= 6 && score >= 3) return 'medium';
    return 'weak';
  }

  const passwordStrength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);
  const passwordTooShort = newPassword.length > 0 && newPassword.length < 6;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const passwordsOk = newPassword.length >= 6 && hasUppercase && !confirmMismatch;
  const canSubmit = !!token && passwordsOk && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setInfoMsg("");

    if (!token) {
      setErrorMsg("Invalid reset link");
      return;
    }

    // Client-side minimal validation
    if (!passwordsOk) {
      firstInvalidRef.current?.focus();
      if (passwordTooShort) setErrorMsg('Password must be at least 6 characters.');
      else if (!hasUppercase) setErrorMsg('Password must contain at least one uppercase letter (A-Z).');
      else if (confirmMismatch) setErrorMsg('Passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ token, new_password: newPassword, confirm_password: confirmPassword }),
      });

      if (res.status === 400) {
        // Do not leak backend specific 400 body; show generic invalid/expired link
        await res.text();
        setErrorMsg("Invalid or expired link. Please request a new one.");
        return;
      }
      if (!res.ok) {
        setErrorMsg("Something went wrong. Try again.");
        return;
      }

      setInfoMsg("Password reset successful");
      setTimeout(() => setLocation("/login"), 1200);
    } catch {
      setErrorMsg("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <Layout>
        <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md text-center space-y-4">
            <Card className="bg-[hsl(215,15%,18%)] border-gray-700 shadow-md">
              <CardHeader>
                <CardTitle className="text-white">Invalid reset link</CardTitle>
                <CardDescription className="text-[hsl(210,9%,80%)]">The link is missing or malformed.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link href="/forgot-password" className="text-[hsl(193,100%,55%)] hover:underline">Forgot Password</Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO title="Reset Password | TradeSagePro" description="Set a new secure password for your TradeSagePro account." canonical="https://tradesagepro.com/reset-password" noindex nofollow />
      <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="bg-[hsl(215,15%,18%)] border-gray-700 shadow-md">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-white">Reset Password</CardTitle>
              <CardDescription className="text-[hsl(210,9%,80%)]">Set a strong new password.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="new" className="text-white">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new"
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      ref={firstInvalidRef}
                      className="pr-10 bg-[hsl(226,26%,7%)] border-gray-600 text-white placeholder:text-gray-400 focus:border-[hsl(193,100%,50%)] focus:ring-[hsl(193,100%,50%)]"
                    />
                    <button type="button" onClick={() => setShowNew(v => !v)} className="absolute inset-y-0 right-0 px-2 text-gray-400 hover:text-white">{showNew ? 'Hide' : 'Show'}</button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm" className="text-white">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pr-10 bg-[hsl(226,26%,7%)] border-gray-600 text-white placeholder:text-gray-400 focus:border-[hsl(193,100%,50%)] focus:ring-[hsl(193,100%,50%)]"
                    />
                    <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute inset-y-0 right-0 px-2 text-gray-400 hover:text-white">{showConfirm ? 'Hide' : 'Show'}</button>
                  </div>
                </div>

                {/* Minimal rules + visual strength (visual only) */}
                {newPassword.length > 0 && (
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded bg-gray-700 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${
                            passwordStrength === 'strong'
                              ? 'bg-green-500 w-full'
                              : passwordStrength === 'medium'
                                ? 'bg-amber-400 w-2/3'
                                : 'bg-red-500 w-1/3'
                          }`}
                        />
                      </div>
                      <span className={`text-[10px] ${
                        passwordStrength === 'strong'
                          ? 'text-green-400'
                          : passwordStrength === 'medium'
                            ? 'text-amber-300'
                            : 'text-red-400'
                      }`}>
                        {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                      </span>
                    </div>
                    {passwordTooShort && (
                      <p className="text-red-400">Password must be at least 6 characters.</p>
                    )}
                    {!passwordTooShort && !hasUppercase && (
                      <p className="text-red-400">Password must contain at least one uppercase letter (A-Z).</p>
                    )}
                    {!passwordTooShort && hasUppercase && !confirmMismatch && newPassword.length >= 6 && (
                      <p className="text-[hsl(210,9%,70%)]">Optional: add numbers or symbols for a stronger password.</p>
                    )}
                    {confirmMismatch && (
                      <p className="text-red-400">Passwords do not match.</p>
                    )}
                  </div>
                )}

                {errorMsg && (
                  <div className="text-sm bg-red-600/20 border border-red-500/50 text-white rounded px-3 py-2">
                    {errorMsg}
                  </div>
                )}
                {infoMsg && (
                  <div className="text-sm bg-green-600/20 border border-green-500/50 text-white rounded px-3 py-2">
                    {infoMsg}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] hover:bg-[hsl(193,90%,45%)] transition-all duration-300 font-semibold"
                >
                  {submitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-[hsl(226,26%,7%)] border-t-transparent rounded-full animate-spin" />
                      <span>Saving...</span>
                    </div>
                  ) : (
                    "Reset password"
                  )}
                </Button>

                <div className="text-center text-sm mt-2">
                  <Link href="/login" className="text-[hsl(193,100%,55%)] hover:underline">Back to login</Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
